import type { Account } from "bonusx-api-main-manager";
import isUndefined from "lodash/isUndefined";
import toString from "lodash/toString";
import { GTMUserDataAdapter } from "../adapter/GTMUserDataAdapter";
import type { GTM } from "./GTMInterfaces";
import { birthDateToNumber } from "./util/birthDateToNumber";
import { encodeToSha1 } from "./util/encode";
import { excludeEmptyValues } from "./util/excludeEmptyValues";
import { lowerCaseTrim } from "./util/lowerCaseTrim";
import { lowerCaseWithoutSpaces } from "./util/lowerCaseWithoutSpaces";
import { stripNotNumeric } from "./util/stripNotNumeric";

interface IUserData {
  user_email: string;
  user_phone: string;
  user_firstname: string;
  user_lastname: string;
  user_birthdate: string;
  user_gender: string;
  user_address: string;
  user_city_fb: string;
  user_city_gads: string;
  user_region: string;
  user_cap: string;
  user_country: string;
}

interface IUserDataHashed {
  user_email_hash: string;
  user_phone_hash: string;
  user_firstname_hash: string;
  user_lastname_hash: string;
  user_birthdate_hash: string;
  user_gender_hash: string;
  user_address_hash: string;
  user_city_fb_hash: string;
  user_city_gads_hash: string;
  user_region_hash: string;
  user_cap_hash: string;
  user_country_hash: string;
}

export class BuildGtmUserData {
  private userId: string;
  private userEmail: string;
  private userPhone: string;
  private firstName: string;
  private lastName: string;
  private birthDate: string;
  private address: string;
  private gender: string;
  private cityFb: string;
  private cityGads: string;
  private region: string;
  private postalCode: string;
  private country: string;
  constructor(account: Account) {
    const userDataAdapter = new GTMUserDataAdapter(account);

    this.userId = toString(userDataAdapter.getId());
    this.userEmail = lowerCaseTrim(userDataAdapter.getEmail());
    this.userPhone = stripNotNumeric(userDataAdapter.getPhone());
    this.firstName = lowerCaseTrim(userDataAdapter.getFirstName());
    this.lastName = lowerCaseTrim(userDataAdapter.getLastName());
    this.birthDate = birthDateToNumber(userDataAdapter.getBirthDate());
    this.address = lowerCaseTrim(userDataAdapter.getAddress());
    this.gender = lowerCaseTrim(userDataAdapter.getGender());
    this.cityFb = lowerCaseWithoutSpaces(userDataAdapter.getCity());
    this.cityGads = lowerCaseTrim(userDataAdapter.getCity());
    this.region = lowerCaseTrim(userDataAdapter.getRegion());
    this.postalCode = toString(userDataAdapter.getPostalCode());
    this.country = lowerCaseTrim(userDataAdapter.getCountry());
  }
  private getUserData(): Map<string, string> {
    const userData: Partial<IUserData> = excludeEmptyValues({
      user_email: this.userEmail,
      user_phone: this.userPhone,
      user_firstname: this.firstName,
      user_lastname: this.lastName,
      user_birthdate: this.birthDate,
      user_gender: this.gender,
      user_address: this.address,
      user_city_fb: this.cityFb,
      user_city_gads: this.cityGads,
      user_region: this.region,
      user_cap: this.postalCode,
      user_country: this.country,
    });

    const entriesOfUserData = Object.entries(userData);
    const mapUserData = new Map(entriesOfUserData);

    return mapUserData;
  }
  private getHashUserDataReducer() {
    const userData = this.getUserData();

    return function hashUserDataReducer(acc: Partial<IUserDataHashed>, key: string): Partial<IUserDataHashed> {
      const value = userData.get(key);
      if (isUndefined(value)) return acc;

      const encodedValue = encodeToSha1(value);
      const item = { [`${key}_hash`]: encodedValue };

      return (acc = { ...acc, ...item });
    };
  }

  private hashUserData(): Partial<IUserDataHashed> {
    const userData = this.getUserData();
    const userDataKeys: string[] = Array.from(userData.keys());
    return userDataKeys.reduce(this.getHashUserDataReducer(), {} as Partial<IUserDataHashed>);
  }
  fromAccount(): Partial<GTM.UserData> {
    const userDataMap = Object.fromEntries(this.getUserData());
    const hashUserData = this.hashUserData();

    const userData: Partial<GTM.UserData> = {
      user_id: this.userId,
      ...userDataMap,
      ...hashUserData,
    };
    return userData;
  }
}
